import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import { timelineApi } from '../services/timelineApi';
import { TimelineEvent, TimelineFilters } from '../types/Timeline';
import Layout from './Layout';
import api from '../services/api';

const Timeline: React.FC = () => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TimelineFilters>({
    page: 1,
    limit: 50,
    module: '',
    action: '',
    leadId: '',
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState<Array<{id: number, firstName: string, lastName: string}>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const fetchEvents = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setEvents([]); // Clear existing events for fresh load
        setCurrentPage(1); // Reset to page 1 for fresh load
      }
      setError(null);
      
      // For load more, increment the page number
      const currentFilters = isLoadMore 
        ? { ...filters, page: currentPage + 1 }
        : { ...filters, page: 1 };
      
      console.log('Sending timeline filters:', currentFilters);
      const response = await timelineApi.getTimelineEvents(currentFilters);
      
      if (response.success) {
        if (isLoadMore) {
          setEvents(prev => [...prev, ...response.data]);
          setCurrentPage(prev => prev + 1);
        } else {
          setEvents(response.data);
          setCurrentPage(1);
        }
        
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setHasMore(response.pagination.hasNextPage);
        }
      } else {
        setError(response.message || 'Failed to fetch timeline events');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch timeline events');
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [filters]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/user/all');
      if (response.data.success) {
        setUsers(response.data.users.map((user: any) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName
        })));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchUsers();
  }, [fetchEvents, fetchUsers]);

  // Scroll detection for back to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleFilterChange = (key: keyof TimelineFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    setCurrentPage(1);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchEvents(true);
    }
  };

  const toggleEventExpansion = (eventId: number) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'update': return 'bg-blue-100 text-blue-800';
      case 'delete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'leads': return '👥';
      case 'tasks': return '✅';
      case 'references': return '🤝';
      case 'calls': return '📞';
      case 'emails': return '📧';
      case 'documents': return '📄';
      case 'forms': return '📋';
      case 'notes': return '📝';
      default: return '📌';
    }
  };

  const isEventExpanded = (eventId: number) => expandedEvents.has(eventId);

  const handleDeleteEvent = async (eventId: number) => {
    try {
      const response = await timelineApi.deleteTimelineEvent(eventId);
      if (response.success) {
        setEvents(prev => prev.filter(event => event.id !== eventId));
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      } else {
        setError(response.message || 'Failed to delete timeline event');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete timeline event');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEvents.size === 0) return;
    
    try {
      const response = await timelineApi.bulkDeleteTimelineEvents(Array.from(selectedEvents));
      if (response.success) {
        setEvents(prev => prev.filter(event => !selectedEvents.has(event.id)));
        setSelectedEvents(new Set());
        setShowBulkActions(false);
      } else {
        setError(response.message || 'Failed to delete timeline events');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete timeline events');
    }
  };

  const toggleEventSelection = (eventId: number) => {
    setSelectedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const selectAllEvents = () => {
    setSelectedEvents(new Set(events.map(event => event.id)));
  };

  const clearSelection = () => {
    setSelectedEvents(new Set());
  };

  if (loading) {
    return (
      <Layout title="Timeline" subtitle="Track all activities across your organization">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Timeline" subtitle="Track all activities across your organization">
      <div className="space-y-6">
        {/* Header with Search and Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search timeline events..."
                value={filters.leadId || ''}
                onChange={(e) => handleFilterChange('leadId', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>
          
          <div className="flex items-center space-x-2">
            {events.length > 0 && (
              <button
                onClick={selectedEvents.size === events.length ? clearSelection : selectAllEvents}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {selectedEvents.size === events.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Module</label>
                <select
                  value={filters.module || ''}
                  onChange={(e) => handleFilterChange('module', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Modules</option>
                  <option value="leads">Leads</option>
                  <option value="tasks">Tasks</option>
                  <option value="references">References</option>
                  <option value="calls">Calls</option>
                  <option value="emails">Emails</option>
                  <option value="documents">Documents</option>
                  <option value="forms">Forms</option>
                  <option value="notes">Notes</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <select
                  value={filters.action || ''}
                  onChange={(e) => handleFilterChange('action', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Actions</option>
                  <option value="create">Create</option>
                  <option value="update">Update</option>
                  <option value="delete">Delete</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <select
                  value={filters.userId || ''}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Users</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id.toString()}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  min={filters.startDate || undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setFilters({
                    page: 1,
                    limit: 50,
                    module: '',
                    action: '',
                    leadId: '',
                    userId: '',
                    startDate: '',
                    endDate: ''
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors mr-2"
              >
                Clear Filters
              </button>
              <button
                onClick={() => fetchEvents()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedEvents.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900">
                  {selectedEvents.size} event{selectedEvents.size > 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Timeline Events */}
        {events.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
              <ClockIcon className="h-24 w-24" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Events</h3>
            <p className="text-gray-500">No activities have been recorded yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => {
              const isExpanded = isEventExpanded(event.id);
              const hasChanges = event.changes && Object.keys(event.changes).length > 0;
              const isCollapsible = event.metadata?.isCollapsible || false;

              return (
                <div key={event.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start space-x-4">
                    {/* Checkbox for bulk selection */}
                    <div className="flex-shrink-0 pt-2">
                      <input
                        type="checkbox"
                        checked={selectedEvents.has(event.id)}
                        onChange={() => toggleEventSelection(event.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>

                    {/* Module Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-lg">
                        {getModuleIcon(event.module)}
                      </div>
                    </div>

                    {/* Event Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(event.action)}`}>
                          {event.action.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {event.module.toUpperCase()}
                        </span>
                        {event.leadName && (
                          <span className="text-sm text-gray-500">
                            • {event.leadName}
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <div className="text-gray-700 mb-3">
                        {event.description}
                      </div>

                      {/* Changes (if any) */}
                      {hasChanges && (
                        <div className="mb-3">
                          {isExpanded || !isCollapsible ? (
                            <div className="bg-gray-50 rounded-lg p-3">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Changes:</h4>
                              <div className="space-y-1">
                                {Object.entries(event.changes || {}).map(([field, change]) => (
                                  <div key={field} className="text-sm">
                                    <span className="font-medium">{field}:</span>
                                    <span className="text-gray-600 ml-1">
                                      {change.from || 'empty'} → {change.to || 'empty'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleEventExpansion(event.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Show {Object.keys(event.changes || {}).length} changes
                            </button>
                          )}
                        </div>
                      )}

                      {/* Metadata */}
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="mb-3">
                          {isExpanded || !isCollapsible ? (
                            <div className="bg-blue-50 rounded-lg p-3">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Details:</h4>
                              <div className="space-y-1">
                                {Object.entries(event.metadata).map(([key, value]) => (
                                  <div key={key} className="text-sm">
                                    <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                    <span className="text-gray-600 ml-1">{String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleEventExpansion(event.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Show details
                            </button>
                          )}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-4 w-4" />
                          <span>{event.userName || 'Unknown User'}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{formatDate(event.createdAt)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex-shrink-0 flex items-center space-x-2">
                      {/* Expand/Collapse Button */}
                      {(isCollapsible || (hasChanges && isCollapsible)) && (
                        <button
                          onClick={() => toggleEventExpansion(event.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => {
                          setEventToDelete(event.id);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete timeline event"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {events.length > 0 && hasMore && (
          <div className="flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Loading...</span>
                </>
              ) : (
                <span>Load More Events</span>
              )}
            </button>
          </div>
        )}

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
            title="Back to top"
          >
            <ArrowUpIcon className="h-6 w-6" />
          </button>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Timeline Event</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete this timeline event? This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-center space-x-4 mt-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setEventToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => eventToDelete && handleDeleteEvent(eventToDelete)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Timeline;
